import { Modal, ModalProps, Space } from 'antd';
import React, { useState } from 'react';
import InfiniteTable, { ColumnsType } from '../InfiniteTable';
import {
  Diagnostic,
  DiagnosticsQuery,
} from '@/core/domain/workorder/Diagnostics/Diagnostics.types';
import { useIntl } from 'react-intl';
import moment from 'moment';
import EditIcon from '../EditIcon';
import { useDiagnostics } from '@/core/domain/workorder/Diagnostics/Diagnostics.hooks';
import AppPageHeader from '../AppPageHeader';
import { useDebounce } from '@/hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { getTableScrollX } from '@/utils/table';

interface Props extends ModalProps {
  onCancel: VoidFunction;
  queryFilter?: DiagnosticsQuery;
  enabled?: boolean;
}
const DiagnosticListModal: React.FC<Props> = ({
  onCancel,
  queryFilter,
  enabled,
  ...props
}) => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<DiagnosticsQuery>({
    ...queryFilter,
    order: 'DESC',
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } = useDiagnostics(
    queryDebounce,
    enabled && props.open,
  );

  const diagnostics = flatInfiniteData(data);

  const columns: ColumnsType<Diagnostic> = [
    {
      key: 'customer',
      dataIndex: ['customer', 'name'],
      title: intl.formatMessage({ id: 'diagnostics.list.header.customer' }),
      width: 200,
      ellipsis: true,
    },
    {
      key: 'creation',
      dataIndex: 'createdAt',
      title: intl.formatMessage({ id: 'diagnostics.list.header.creation' }),
      width: 150,
      ellipsis: true,
      render: (text) => moment(text).format('DD/MM/YYYY'),
    },
    {
      key: 'plate',
      dataIndex: ['vehicle', 'licensePlate'],
      title: intl.formatMessage({ id: 'diagnostics.list.header.plate' }),
      width: 150,
      ellipsis: true,
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 100,
      ellipsis: true,
      align: 'right',
      fixed: 'right',
      render: (_text, record) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () =>
                window.open(
                  `/app/movements/diagnostics/${record.id}`,
                  '_blank',
                ),
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <Modal {...props} width={900} onCancel={onCancel}>
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
      />

      <InfiniteTable<Diagnostic>
        loading={isFetching}
        size="small"
        rowKey={(row) => row.id}
        dataSource={diagnostics}
        columns={columns}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        scroll={{ x: getTableScrollX(columns) }}
      />
    </Modal>
  );
};

export default DiagnosticListModal;
